import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PresetCategoryListItem, CreatePresetCategoryRequest, UpdatePresetCategoryRequest } from '../../data/models/preset-categories.models';
import { PresetCategoriesService } from '../../data/services/preset-categories.service';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';

@Component({
  selector: 'app-preset-categories',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './preset-categories.html',
  styleUrl: './preset-categories.scss',
})
export class PresetCategories implements OnInit {
  private readonly presetCategoriesService = inject(PresetCategoriesService);
  private readonly globalAlertService = inject(GlobalAlertService);
  private readonly fb = inject(FormBuilder);

  categories: PresetCategoryListItem[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 20;
  totalPages = 0;

  isLoading = false;
  errorMessage = '';

  isModalOpen = false;
  isEditMode = false;
  isSaving = false;
  isDeleting = false;
  selectedCategoryId: number | null = null;
  deletingCategoryId: number | null = null;

  isDeleteModalOpen = signal(false);
  deletingCategory: PresetCategoryListItem | null = null;

  searchTerm = '';
  private searchSubject = new Subject<string>();

  statusFilter: 'All' | 'Active' | 'Inactive' = 'All';

  categoryForm: FormGroup;

  constructor() {
    this.categoryForm = this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      isActive: [true],
    });

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.pageNumber = 1;
        this.loadCategories();
      });
  }

  ngOnInit(): void {
    this.loadCategories();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onStatusFilterChange(): void {
    this.pageNumber = 1;
    this.loadCategories();
  }

  onPageSizeChange(): void {
    this.pageNumber = 1;
    this.loadCategories();
  }

  loadCategories(): void {
    this.isLoading = true;
    this.errorMessage = '';

    let isActiveParam: boolean | undefined = undefined;
    if (this.statusFilter === 'Active') {
      isActiveParam = true;
    } else if (this.statusFilter === 'Inactive') {
      isActiveParam = false;
    }

    this.presetCategoriesService
      .getCategories(this.pageNumber, this.pageSize, this.searchTerm, isActiveParam)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.categories = response.data.categories || [];
            this.totalCount = response.data.totalCount || 0;
            this.totalPages =
              response.data.totalPages ||
              Math.ceil(this.totalCount / this.pageSize);
          } else {
            this.errorMessage = response.message || 'Failed to load preset categories.';
            this.categories = [];
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load preset categories.';
          this.categories = [];
          this.isLoading = false;
        },
      });
  }

  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadCategories();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadCategories();
    }
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.selectedCategoryId = null;
    this.categoryForm.reset({
      name: '',
      description: '',
      isActive: true,
    });
    this.errorMessage = '';
    this.isModalOpen = true;
  }

  openEditModal(category: PresetCategoryListItem): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.presetCategoriesService.getCategory(category.presetCategoryId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          const detail = response.data;
          this.isEditMode = true;
          this.selectedCategoryId = detail.presetCategoryId;

          this.categoryForm.reset({
            name: detail.name,
            description: detail.description || '',
            isActive: detail.isActive,
          });

          this.isModalOpen = true;
        } else {
          this.globalAlertService.showError(response.message || 'Failed to load category details.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.globalAlertService.showError(err?.error?.message || 'Failed to load category details.');
      },
    });
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.categoryForm.markAsPristine();
    this.categoryForm.markAsUntouched();
  }

  saveCategory(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const formValue = this.categoryForm.getRawValue();
    const name = (formValue.name || '').trim();
    let description = formValue.description ? formValue.description.trim() : null;
    if (description === '') {
      description = null;
    }
    const isActive = !!formValue.isActive;

    if (this.isEditMode && this.selectedCategoryId !== null) {
      const payload: UpdatePresetCategoryRequest = {
        name,
        description,
        isActive,
      };

      this.presetCategoriesService
        .updateCategory(this.selectedCategoryId, payload)
        .subscribe({
          next: (response) => {
            this.isSaving = false;
            if (response.success) {
              this.globalAlertService.showSuccess(response.message || 'Preset category updated successfully.');
              this.loadCategories();
              this.closeModal();
            } else {
              this.errorMessage = response.message || 'Failed to update preset category.';
            }
          },
          error: (err) => {
            this.isSaving = false;
            this.errorMessage = err?.error?.message || 'Failed to update preset category.';
          },
        });
    } else {
      const payload: CreatePresetCategoryRequest = {
        name,
        description,
        isActive,
      };

      this.presetCategoriesService.createCategory(payload).subscribe({
        next: (response) => {
          this.isSaving = false;
          if (response.success) {
            this.globalAlertService.showSuccess(response.message || 'Preset category created successfully.');
            this.pageNumber = 1;
            this.loadCategories();
            this.closeModal();
          } else {
            this.errorMessage = response.message || 'Failed to create preset category.';
          }
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage = err?.error?.message || 'Failed to create preset category.';
        },
      });
    }
  }

  openDeleteModal(category: PresetCategoryListItem): void {
    this.deletingCategory = category;
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.deletingCategory = null;
  }

  confirmDeleteCategory(): void {
    if (!this.deletingCategory) return;

    const categoryId = this.deletingCategory.presetCategoryId;
    if (categoryId === 1) {
      this.globalAlertService.showError('The Uncategorized category cannot be deleted.', 'Action Not Allowed');
      this.closeDeleteModal();
      return;
    }

    this.deletingCategoryId = categoryId;
    this.isDeleting = true;

    this.presetCategoriesService.deleteCategory(categoryId).subscribe({
      next: (response) => {
        this.isDeleting = false;
        if (response.success) {
          this.globalAlertService.showSuccess(response.message || 'Preset category deleted successfully.');
          if (this.categories.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }
          this.loadCategories();
          this.closeDeleteModal();
        } else {
          this.globalAlertService.showError(response.message || 'Failed to delete preset category.');
          this.closeDeleteModal();
        }
        this.deletingCategoryId = null;
      },
      error: (err) => {
        this.isDeleting = false;
        this.globalAlertService.showError(err?.error?.message || 'Failed to delete preset category.');
        this.deletingCategoryId = null;
        this.closeDeleteModal();
      },
    });
  }

  onDeleteCategory(category: PresetCategoryListItem): void {
    if (category.presetCategoryId === 1) {
      this.globalAlertService.showError('The Uncategorized category cannot be deleted.', 'Action Not Allowed');
      return;
    }
    this.openDeleteModal(category);
  }

  clearError(): void {
    this.errorMessage = '';
  }

  get nameControl() {
    return this.categoryForm.get('name');
  }
}
